import React, { CSSProperties, useEffect, useMemo, useState } from 'react';
import {
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { InsightsAccordionWrapper } from './style';
import { colors } from 'crono-fe-common/theme';
import {
  FundingData,
  LinkedinActivity,
  LinkedinActivityType,
  LinkedinCompanyExtendedInfo,
  LinkedinJobPost,
  LinkedinProfileExtendedInfo,
  LinkedinWorkExperience,
} from 'crono-fe-common/types/crono-extension/linkedin';
import { ExpandableText } from 'crono-fe-common/components/ExpandableText';
import { Prospect } from 'crono-fe-common/types/prospect';
import { Constants } from 'crono-fe-common/constants/constants';
import IconFilledDownArrow from 'crono-fe-common/icons/Icon-filled-rounded-down-arrow';
import InsightsAccordionFooter from './insightsAccordionFooter';
import CronoCheckbox from 'crono-fe-common/components/CronoCheckbox';
import { getDateDiff, transformToValidUrl } from 'crono-fe-common/utils';
import IconRedirect from 'crono-fe-common/icons/Icon-Redirect';
import IconBottomArrow from 'crono-fe-common/icons/Icon-Bottom-Arrow';
import { ProspectLinkedin } from 'crono-fe-common/types/prospectLinkedin';
import IconQuestionMark from 'crono-fe-common/icons/Icon-Question-Mark';
import { Analytics } from 'crono-fe-common/hooks/crono-extension/gateway';

export enum InsightType {
  PROFILE_VIEW = 'PROFILE_VIEW',
  SOCIAL_PRESENCE = 'SOCIAL_PRESENCE',
  KEY_TOPICS = 'KEY_TOPICS',
  JOB_OPENINGS = 'JOB_OPENINGS',
  COMPANY_NEWS = 'COMPANY_NEWS',
  FUNDING_ROUNDS = 'FUNDING_ROUNDS',
}

interface IProps {
  options?:
    | LinkedinProfileExtendedInfo
    | LinkedinCompanyExtendedInfo
    | FundingData
    | LinkedinActivity[]
    | string[]
    | { jobs: LinkedinJobPost[]; total: number }
    | null;
  icon: JSX.Element;
  type: InsightType;
  title?: string;
  collapsedHeight?: string;
  style?: CSSProperties;
  fetchOptions?: () => Promise<void>;
  fetchOptionsLoading?: boolean;
  prospect?: Prospect | ProspectLinkedin | null;
  publicId?: string | null;
  growth?: string | null;
  companyPublicId?: string | null;
  selectedArray?: number[];
  setSelectedArray?: (newArray: number[]) => void;
  toggleSelectedOption?: () => void;
  isSelected?: boolean;
  analytics: Analytics | null;
}

const InsightsAccordion = ({
  options,
  icon,
  type,
  title,
  collapsedHeight,
  style,
  fetchOptions,
  fetchOptionsLoading,
  prospect,
  publicId,
  growth,
  companyPublicId,
  selectedArray,
  setSelectedArray,
  isSelected,
  toggleSelectedOption,
  analytics,
}: IProps) => {
  const [expanded, setExpanded] = useState<boolean>(false);

  const sortedExperiences: LinkedinWorkExperience[] = useMemo(() => {
    if (!options || !('workExperience' in options)) return [];

    const exp = options?.workExperience;
    if (options?.workExperience?.length > 0 && companyPublicId) {
      const experienceInCronoRegisteredCompany: LinkedinWorkExperience[] =
        options?.workExperience?.filter(
          (experience: LinkedinWorkExperience) => {
            return experience.company.publicId === companyPublicId;
          },
        );
      const currentExperience = experienceInCronoRegisteredCompany.findIndex(
        (exp) => {
          return !exp.timePeriod?.endDate;
        },
      );
      if (currentExperience !== -1) {
        experienceInCronoRegisteredCompany.unshift(
          experienceInCronoRegisteredCompany.splice(currentExperience, 1)[0],
        );
      }

      return [
        ...experienceInCronoRegisteredCompany,
        ...exp.filter((experience: LinkedinWorkExperience) => {
          return experience.company.publicId !== companyPublicId;
        }),
      ];
    }
    return exp;
  }, [options, companyPublicId]);

  const onlyOneExperience = useMemo(() => {
    return Boolean(
      options &&
      'workExperience' in options &&
      options?.workExperience &&
      options?.workExperience?.length === 1,
    );
  }, [options]);

  const insightHeaderTitleCounter = (
    option: typeof options,
    type: InsightType,
  ) => {
    let counter;
    switch (type) {
      case InsightType.SOCIAL_PRESENCE:
        if (option && Array.isArray(option) && option.length) {
          counter = option?.length;
        }
        break;
      case InsightType.KEY_TOPICS:
        if (option && Array.isArray(option)) {
          counter = option?.length;
        }
        break;
      case InsightType.JOB_OPENINGS:
        if (
          option &&
          'jobs' in option &&
          option?.jobs &&
          option?.jobs?.length
        ) {
          counter = option?.jobs?.length;
        }
        break;
      case InsightType.COMPANY_NEWS:
        if (
          option &&
          'activities' in option &&
          option?.activities?.activities &&
          option?.activities?.activities?.length
        ) {
          counter = option?.activities?.activities?.length;
        }
        break;
      case InsightType.FUNDING_ROUNDS:
        if (
          option &&
          isFundingRoundsVisible(option as FundingData) &&
          'numberOfFundingRounds' in option
        ) {
          counter = option.numberOfFundingRounds;
        }
    }
    return counter ? (
      <span className={`counter ${type}-counter`}>{counter}</span>
    ) : null;
  };

  const renderCronoCheckbox = useMemo(() => {
    if (!options) return true;

    return (
      (type === InsightType.SOCIAL_PRESENCE &&
        Array.isArray(options) &&
        options.length > 0) ||
      (type === InsightType.COMPANY_NEWS &&
        'activities' in options &&
        (options.activities?.activities?.length ?? 0) > 0) ||
      (type === InsightType.JOB_OPENINGS &&
        'jobs' in options &&
        options.jobs.length > 0) ||
      (type === InsightType.KEY_TOPICS &&
        Array.isArray(options) &&
        options.length > 0) ||
      (type === InsightType.FUNDING_ROUNDS &&
        isFundingRoundsVisible(options as FundingData)) ||
      (type === InsightType.PROFILE_VIEW &&
        sortedExperiences &&
        sortedExperiences.length > 0)
    );
  }, [options, type]);

  const [toBeSelectedAfterFetch, setToBeSelectedAfterFetch] =
    useState<boolean>(false);

  useEffect(() => {
    if (setSelectedArray && options && toBeSelectedAfterFetch) {
      if (
        (type === InsightType.SOCIAL_PRESENCE &&
          Array.isArray(options) &&
          options.length > 0) ||
        (type === InsightType.JOB_OPENINGS &&
          'jobs' in options &&
          options.jobs.length > 0)
      ) {
        setSelectedArray(selectedArray?.length === 0 ? [0] : []);
      }
      setToBeSelectedAfterFetch(false);
    }
  }, [options]);

  const [socialPresenceFilter, setSocialPresenceFilter] = useState<
    LinkedinActivityType | 'all'
  >('all');

  const socialPresenceJSX = useMemo(() => {
    if (type !== InsightType.SOCIAL_PRESENCE || !options) return null;

    const activitiesToRender: (LinkedinActivity & { parentIndex: number })[] =
      [];
    const activities = options as LinkedinActivity[];
    activities.forEach((activity, index) => {
      if (socialPresenceFilter === 'all') {
        activitiesToRender.push({ ...activity, parentIndex: index });
      } else if (activity.type === socialPresenceFilter) {
        activitiesToRender.push({ ...activity, parentIndex: index });
      }
    });

    if (activitiesToRender.length > 0) {
      return activitiesToRender
        ?.sort(
          (a, b) =>
            (b.calculatedDate ? new Date(b.calculatedDate).getTime() : 0) -
            (a.calculatedDate ? new Date(a.calculatedDate).getTime() : 0),
        )
        .map(({ parentIndex, ...activity }) => {
          return (
            <div
              key={activity.calculatedDate + '-' + parentIndex}
              className="insight-social-activity-container"
            >
              {selectedArray !== undefined && setSelectedArray !== undefined ? (
                <CronoCheckbox
                  checked={selectedArray.includes(parentIndex)}
                  onClick={() =>
                    setSelectedArray(
                      selectedArray.includes(parentIndex)
                        ? selectedArray.filter((index) => index !== parentIndex)
                        : [...selectedArray, parentIndex],
                    )
                  }
                  sx={{
                    height: 'fit-content',
                  }}
                />
              ) : null}
              <div className={'insight-social-activity'}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <Typography
                      className={
                        activity.type === 'comment'
                          ? 'linkedin-comment'
                          : 'linkedin-post'
                      }
                    >
                      {activity.type.charAt(0).toUpperCase() +
                        activity.type.slice(1)}
                    </Typography>
                    <Typography fontSize={'11px'} color={colors.grey11}>
                      - {activity.type === 'comment' ? 'commented' : 'posted'}{' '}
                      {activity.date} ago
                    </Typography>
                  </div>
                  {activity.url && (
                    <span
                      className="redirect-icon-wrapper activity-url"
                      onClick={() =>
                        window.open(
                          transformToValidUrl(activity.url) ?? undefined,
                          '_blank',
                        )
                      }
                    >
                      <IconRedirect
                        style={{
                          height: 16,
                          width: 16,
                          flexShrink: 0,
                        }}
                      />
                    </span>
                  )}
                </div>
                <ExpandableText
                  text={activity?.content}
                  lines={2}
                  showMoreLabel="More"
                  showLessLabel="Less"
                  className="text-xs"
                />
              </div>
            </div>
          );
        });
    } else {
      return (
        <div className="social-presence-empty-state">
          <IconQuestionMark />
          <Typography
            fontSize={'14px'}
            color={colors.grey11}
            textAlign={'center'}
          >
            No LinkedIn activity found <br />
            in this category yet.
          </Typography>
        </div>
      );
    }
  }, [options, socialPresenceFilter, selectedArray, setSelectedArray, type]);

  return (
    <InsightsAccordionWrapper
      id="insights-accordion-wrapper"
      style={{ height: expanded ? 'fit-content' : collapsedHeight, ...style }}
    >
      <div
        className={`insights-top-header ${type} ${type}-header ${
          expanded ? `${type}-header-expanded` : ''
        }`}
      >
        <div className="insights-icon-position-container">
          {renderCronoCheckbox &&
          selectedArray !== undefined &&
          setSelectedArray !== undefined ? (
            <span className={`insight-icon-wrapper ${type}-ICON`}>
              {type === InsightType.SOCIAL_PRESENCE ||
              type === InsightType.COMPANY_NEWS ||
              type === InsightType.JOB_OPENINGS ? (
                <CronoCheckbox
                  checked={selectedArray.length !== 0}
                  onClick={async () => {
                    if (!expanded && !options && fetchOptions) {
                      setExpanded(true);
                      setToBeSelectedAfterFetch(true);
                      await fetchOptions();
                    } else {
                      if (
                        type === InsightType.SOCIAL_PRESENCE &&
                        socialPresenceFilter !== 'all'
                      ) {
                        const firstActivityIdxWithChosenFilter =
                          (options as LinkedinActivity[]).findIndex(
                            (a) => a.type === socialPresenceFilter,
                          ) ?? 0;

                        setSelectedArray(
                          selectedArray.length === 0
                            ? [firstActivityIdxWithChosenFilter]
                            : [],
                        );
                      } else {
                        setSelectedArray(selectedArray.length === 0 ? [0] : []);
                      }
                    }
                  }}
                />
              ) : (
                <CronoCheckbox
                  checked={selectedArray.includes(0)}
                  onClick={() =>
                    setSelectedArray(
                      selectedArray.includes(0)
                        ? selectedArray.filter((index) => index !== 0)
                        : [...selectedArray, 0],
                    )
                  }
                />
              )}
            </span>
          ) : renderCronoCheckbox &&
            isSelected !== undefined &&
            toggleSelectedOption !== undefined ? (
            <span className={`insight-icon-wrapper ${type}-ICON`}>
              <CronoCheckbox
                checked={isSelected}
                onClick={toggleSelectedOption}
              />
            </span>
          ) : (
            <span className={`insight-icon-wrapper ${type}-ICON`}>{icon}</span>
          )}
          {type === InsightType.PROFILE_VIEW && sortedExperiences ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                cursor: onlyOneExperience ? 'default' : 'pointer',
                overflow: 'hidden',
              }}
              onClick={async (ev) => {
                ev.stopPropagation();
                ev.preventDefault();
                if (!onlyOneExperience) {
                  setExpanded((prev) => {
                    if (!prev) {
                      if (analytics) {
                        analytics.track('open-insights-detail', {
                          type: InsightType.PROFILE_VIEW,
                        });
                      }
                    }
                    return !prev;
                  });
                  if (!options && fetchOptions) {
                    await fetchOptions();
                  }
                }
              }}
            >
              <div className="insight-position-experience">
                <Typography fontSize={'12px'} fontWeight={500} noWrap>
                  {sortedExperiences[0]?.position}
                </Typography>
                <Typography fontSize={'12px'} color={colors.grey11} noWrap>
                  {sortedExperiences[0]?.company?.name} -{' '}
                  {sortedExperiences[0]?.timePeriod?.years
                    ? `${sortedExperiences[0]?.timePeriod?.years} ye`
                    : null}
                  {sortedExperiences[0]?.timePeriod?.months
                    ? ` ${sortedExperiences[0]?.timePeriod?.months} mo`
                    : null}{' '}
                  -{' '}
                  {!sortedExperiences[0]?.timePeriod?.endDate
                    ? 'Present'
                    : `${
                        Constants.monthsDictionary[
                          sortedExperiences[0]?.timePeriod?.endDate?.month
                        ]
                      } ${sortedExperiences[0]?.timePeriod?.endDate?.year} `}
                </Typography>
              </div>
              {expanded && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    width: 'fit-content',
                  }}
                >
                  <IconButton
                    style={{
                      transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      height: 'fit-content',
                    }}
                  >
                    <IconBottomArrow />
                  </IconButton>
                </div>
              )}
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                cursor: 'pointer',
              }}
              onClick={async (ev) => {
                ev.stopPropagation();
                ev.preventDefault();
                setExpanded((prev) => {
                  if (!prev) {
                    if (analytics) {
                      analytics.track('open-insights-detail', {
                        type: type,
                      });
                    }
                  }
                  return !prev;
                });
                if (!options && fetchOptions) {
                  await fetchOptions();
                }
              }}
            >
              <Typography
                fontSize={'13px'}
                fontWeight={400}
                noWrap
                display={'flex'}
              >
                {title} {options && insightHeaderTitleCounter(options, type)}
              </Typography>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  width: 'fit-content',
                }}
              >
                {growth ? (
                  <span
                    className={
                      growth?.includes('-')
                        ? 'negative-growth'
                        : 'positive-growth'
                    }
                  >
                    Growth: {growth}
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transform: !growth?.includes('-')
                          ? 'rotate(180deg)'
                          : 'rotate(0deg)',
                      }}
                    >
                      <IconFilledDownArrow
                        color={
                          growth?.includes('-')
                            ? colors.inactive
                            : colors.darkGreen
                        }
                      />
                    </span>
                  </span>
                ) : null}
                <IconButton
                  onClick={async (ev) => {
                    ev.stopPropagation();
                    ev.preventDefault();
                    setExpanded((prev) => {
                      if (!prev) {
                        if (analytics) {
                          analytics.track('open-insights-detail', {
                            type: type,
                          });
                        }
                      }
                      return !prev;
                    });
                    if (!options && fetchOptions) {
                      await fetchOptions();
                    }
                  }}
                  style={{
                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    height: 'fit-content',
                  }}
                >
                  <IconBottomArrow />
                </IconButton>
              </div>
            </div>
          )}
        </div>
        {type === InsightType.PROFILE_VIEW &&
        options &&
        sortedExperiences &&
        sortedExperiences[0]?.description ? (
          <div
            style={{
              backgroundColor: colors.white,
              padding: '8px',
            }}
          >
            <ExpandableText
              text={sortedExperiences[0]?.description}
              lines={2}
              showMoreLabel="More"
              showLessLabel="Less"
              className="text-[13px]"
            />
          </div>
        ) : null}
      </div>
      {/* Profile work experience - 1st is experience in saved company */}
      {type === InsightType.PROFILE_VIEW && expanded ? (
        options && sortedExperiences && sortedExperiences.length > 1 ? (
          sortedExperiences
            .slice(1)
            ?.map((experience: LinkedinWorkExperience, i: number) => {
              return (
                <div
                  className={`insights-inner-top-header ${
                    experience.timePeriod?.endDate ? 'greyed' : ''
                  }`}
                  key={i}
                >
                  <div className="insights-icon-position-container">
                    {renderCronoCheckbox &&
                    selectedArray !== undefined &&
                    setSelectedArray !== undefined ? (
                      <CronoCheckbox
                        checked={selectedArray.includes(i + 1)}
                        onClick={() =>
                          setSelectedArray(
                            selectedArray.includes(i + 1)
                              ? selectedArray.filter((index) => index !== i + 1)
                              : [...selectedArray, i + 1],
                          )
                        }
                      />
                    ) : (
                      <span className={`insight-icon-wrapper ${type}-ICON`}>
                        {icon}
                      </span>
                    )}
                    {type === InsightType.PROFILE_VIEW ? (
                      <div className="insight-position-experience">
                        <Typography fontSize={'12px'} fontWeight={500} noWrap>
                          {experience.position}
                        </Typography>
                        <Typography
                          fontSize={'12px'}
                          color={colors.grey11}
                          noWrap
                        >
                          {experience.company.name} -{' '}
                          {experience.timePeriod?.years
                            ? `${experience.timePeriod?.years} ye`
                            : null}
                          {experience.timePeriod?.months
                            ? ` ${experience.timePeriod?.months} mo`
                            : null}{' '}
                          -{' '}
                          {!experience.timePeriod?.endDate
                            ? 'Present'
                            : `${
                                Constants.monthsDictionary[
                                  experience.timePeriod?.endDate.month
                                ]
                              } ${experience.timePeriod?.endDate.year} `}
                        </Typography>
                      </div>
                    ) : (
                      type
                    )}
                  </div>
                  {type === InsightType.PROFILE_VIEW &&
                  experience.description ? (
                    <div
                      style={{
                        backgroundColor: colors.white,
                        padding: '8px',
                      }}
                    >
                      <ExpandableText
                        text={experience.description}
                        lines={2}
                        showMoreLabel="More"
                        showLessLabel="Less"
                        className="text-[13px]"
                      />
                    </div>
                  ) : null}
                </div>
              );
            })
        ) : (
          <div className="empty-state">
            <Typography fontSize={'12px'} color={colors.grey11}>
              No experience available
            </Typography>
          </div>
        )
      ) : null}
      {/* Social presence */}
      {expanded ? (
        fetchOptionsLoading && type === InsightType.SOCIAL_PRESENCE ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <CircularProgress />
          </div>
        ) : type === InsightType.SOCIAL_PRESENCE ? (
          options && Array.isArray(options) && options.length ? (
            <>
              <div className="tabs-group">
                <IconButton
                  className={
                    socialPresenceFilter === 'all'
                      ? 'tab-selected'
                      : 'tab-not-selected'
                  }
                  onClick={() => setSocialPresenceFilter('all')}
                >
                  <Typography fontWeight={500} fontSize={12}>
                    All
                  </Typography>
                </IconButton>
                <IconButton
                  className={
                    socialPresenceFilter === 'comment'
                      ? 'tab-selected'
                      : 'tab-not-selected'
                  }
                  onClick={() => setSocialPresenceFilter('comment')}
                >
                  <Typography fontWeight={500} fontSize={12}>
                    Comments
                  </Typography>
                </IconButton>
                <IconButton
                  className={
                    socialPresenceFilter === 'post'
                      ? 'tab-selected'
                      : 'tab-not-selected'
                  }
                  onClick={() => setSocialPresenceFilter('post')}
                >
                  <Typography fontWeight={500} fontSize={12}>
                    Posts
                  </Typography>
                </IconButton>
                <IconButton
                  className={
                    socialPresenceFilter === 'event'
                      ? 'tab-selected'
                      : 'tab-not-selected'
                  }
                  onClick={() => setSocialPresenceFilter('event')}
                >
                  <Typography fontWeight={500} fontSize={12}>
                    Events
                  </Typography>
                </IconButton>
              </div>
              <div
                id="insight-social-presence-body"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '0px 4px',
                  boxSizing: 'border-box',
                  gap: '8px',
                  width: '100%',
                  overflow: 'auto',
                  maxHeight: '350px',
                }}
              >
                {socialPresenceJSX}
              </div>
            </>
          ) : (
            <div className="empty-state">
              <Typography fontSize={'12px'} color={colors.grey11}>
                No social presence available
              </Typography>
            </div>
          )
        ) : null
      ) : null}
      {/* Key topics */}
      {expanded ? (
        fetchOptionsLoading && type === InsightType.KEY_TOPICS ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <CircularProgress />
          </div>
        ) : type === InsightType.KEY_TOPICS ? (
          options && Array.isArray(options) && options?.length ? (
            <div
              id="insight-key-topics-body"
              style={{
                display: 'flex',
                flexDirection: 'column',
                padding: '0px 8px 8px 8px',
                boxSizing: 'border-box',
                gap: '8px',
                width: '100%',
              }}
            >
              <Typography fontSize={'12px'} color={colors.grey11}>
                This contact usually talks about:{' '}
                <span style={{ color: colors.black }}>
                  {options?.join(', ')}
                </span>
              </Typography>
            </div>
          ) : (
            <div className="empty-state" style={{ marginBottom: '8px' }}>
              <Typography fontSize={'12px'} color={colors.grey11}>
                No key topics available
              </Typography>
            </div>
          )
        ) : null
      ) : null}
      {/* Job openings */}
      {expanded ? (
        fetchOptionsLoading && type === InsightType.JOB_OPENINGS ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <CircularProgress />
          </div>
        ) : type === InsightType.JOB_OPENINGS ? (
          options &&
          'jobs' in options &&
          options.jobs &&
          options.jobs.length ? (
            <div
              id="insight-job-openings-body"
              style={{
                display: 'flex',
                flexDirection: 'column',
                padding: '0px 4px',
                boxSizing: 'border-box',
                gap: '8px',
                width: '100%',
              }}
            >
              {options.jobs
                ?.sort(
                  (a: LinkedinJobPost, b: LinkedinJobPost) =>
                    new Date(b.date ?? Date.now()).getTime() -
                    new Date(a.date ?? Date.now()).getTime(),
                )
                ?.slice(0, 5)
                ?.map((job: LinkedinJobPost, i: number) => {
                  let jobPostDate = 'No date information available';
                  if (job.date) {
                    jobPostDate = getDateDiff(job.date);
                  }
                  return (
                    <div
                      key={job.date + '-' + i}
                      className="insight-linkedin-activity"
                      style={{
                        flexDirection: 'row',
                      }}
                    >
                      {selectedArray !== undefined &&
                      setSelectedArray !== undefined ? (
                        <CronoCheckbox
                          checked={selectedArray.includes(i)}
                          onClick={() =>
                            setSelectedArray(
                              selectedArray.includes(i)
                                ? selectedArray.filter((index) => index !== i)
                                : [...selectedArray, i],
                            )
                          }
                          sx={{
                            height: 'fit-content',
                          }}
                        />
                      ) : null}
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                          width: '100%',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Typography fontSize={'11px'} color={colors.grey11}>
                            {jobPostDate ?? 'No date information available'}
                          </Typography>
                          {job.jobUrl && (
                            <span
                              className="redirect-icon-wrapper activity-url"
                              onClick={() =>
                                window.open(
                                  transformToValidUrl(job.jobUrl) ?? undefined,
                                  '_blank',
                                )
                              }
                            >
                              <IconRedirect
                                style={{
                                  height: 16,
                                  width: 16,
                                  flexShrink: 0,
                                }}
                              />
                            </span>
                          )}
                        </div>
                        <Typography fontSize={'12px'} fontWeight={500}>
                          {job.title}
                        </Typography>
                        <Typography fontSize={'12px'} color={colors.grey11}>
                          {job.location}
                        </Typography>
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : (
            <div className="empty-state">
              <Typography fontSize={'12px'} color={colors.grey11}>
                No jobs openings available
              </Typography>
            </div>
          )
        ) : null
      ) : null}
      {/* Company News */}
      {expanded ? (
        fetchOptionsLoading && type === InsightType.COMPANY_NEWS ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <CircularProgress />
          </div>
        ) : type === InsightType.COMPANY_NEWS ? (
          options &&
          'activities' in options &&
          options?.activities &&
          options?.activities?.activities &&
          options?.activities?.activities?.length ? (
            <div
              id="insight-company-news-body"
              style={{
                display: 'flex',
                flexDirection: 'column',
                padding: '0px 4px',
                boxSizing: 'border-box',
                gap: '8px',
                width: '100%',
              }}
            >
              {options?.activities?.activities
                ?.sort(
                  (a: LinkedinActivity, b: LinkedinActivity) =>
                    (b.calculatedDate
                      ? new Date(b.calculatedDate).getTime()
                      : 0) -
                    (a.calculatedDate
                      ? new Date(a.calculatedDate).getTime()
                      : 0),
                )
                ?.slice(0, 5)
                .map((activity: LinkedinActivity, i: number) => {
                  return (
                    <div
                      key={activity.calculatedDate + '-' + i}
                      className="insight-linkedin-activity"
                      style={{
                        flexDirection: 'row',
                      }}
                    >
                      {selectedArray !== undefined &&
                      setSelectedArray !== undefined ? (
                        <CronoCheckbox
                          checked={selectedArray.includes(i)}
                          onClick={() =>
                            setSelectedArray(
                              selectedArray.includes(i)
                                ? selectedArray.filter((index) => index !== i)
                                : [...selectedArray, i],
                            )
                          }
                          sx={{
                            height: 'fit-content',
                          }}
                        />
                      ) : null}
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <Typography
                              className={
                                activity.type === 'comment'
                                  ? 'linkedin-job-comment'
                                  : 'linkedin-job-post'
                              }
                            >
                              {activity.type.charAt(0).toUpperCase() +
                                activity.type.slice(1)}
                            </Typography>
                            <Typography fontSize={'11px'} color={colors.grey11}>
                              -{' '}
                              {activity.type === 'comment'
                                ? 'commented'
                                : 'posted'}{' '}
                              {activity.date} ago
                            </Typography>
                          </div>
                          {activity.url && (
                            <span
                              className="redirect-icon-wrapper activity-url"
                              onClick={() =>
                                window.open(
                                  transformToValidUrl(activity.url) ??
                                    undefined,
                                  '_blank',
                                )
                              }
                            >
                              <IconRedirect
                                style={{
                                  height: 16,
                                  width: 16,
                                  flexShrink: 0,
                                }}
                              />
                            </span>
                          )}
                        </div>
                        <ExpandableText
                          text={activity?.content}
                          lines={2}
                          showMoreLabel="More"
                          showLessLabel="Less"
                          className="text-xs"
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : (
            <div className="empty-state">
              <Typography fontSize={'12px'} color={colors.grey11}>
                No news available
              </Typography>
            </div>
          )
        ) : null
      ) : null}
      {/* Funding Rounds */}
      {expanded ? (
        fetchOptionsLoading && type === InsightType.FUNDING_ROUNDS ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <CircularProgress />
          </div>
        ) : type === InsightType.FUNDING_ROUNDS ? (
          isFundingRoundsVisible(options as FundingData) ? (
            <div
              id="insight-company-news-body"
              style={{
                display: 'flex',
                flexDirection: 'column',
                padding: '0px 8px',
                boxSizing: 'border-box',
                gap: '8px',
                width: '100%',
                paddingBottom:
                  type === InsightType.FUNDING_ROUNDS &&
                  expanded &&
                  (!options ||
                    !('crunchbaseUrl' in options) ||
                    !options?.crunchbaseUrl) &&
                  !prospect?.account?.linkedin
                    ? '8px'
                    : '0px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-end',
                }}
              >
                <div
                  style={{ display: 'flex', gap: '4px', alignItems: 'center' }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <Tooltip
                      title={
                        options && 'amount' in options && options.amount
                          ? null
                          : 'The company may not have publicly released this information or the data is incomplete or gated'
                      }
                      enterDelay={400}
                      enterNextDelay={400}
                      arrow
                    >
                      <Typography fontSize={'16px'} fontWeight={500}>
                        {(options && 'amount' in options
                          ? options.amount
                          : undefined) ?? 'Amount unknown'}
                      </Typography>
                    </Tooltip>
                    <Typography
                      fontSize={'11px'}
                      color={colors.fundingRoundsCounter}
                      fontWeight={500}
                      style={{
                        backgroundColor: colors.fundingRounds,
                        borderRadius: '12px',
                        padding: '2px 4px',
                        width: 'fit-content',
                      }}
                    >
                      {(options && 'fundingRound' in options
                        ? options.fundingRound
                        : undefined) ?? ''}
                    </Typography>
                  </div>
                </div>
                <Typography fontSize={'11px'} color={colors.grey11}>
                  Last Round:{' '}
                  {options && 'announcedDate' in options
                    ? options.announcedDate
                    : undefined}
                </Typography>
              </div>
            </div>
          ) : (
            <div className="empty-state" style={{ marginBottom: '8px' }}>
              <Typography fontSize={'12px'} color={colors.grey11}>
                No funding rounds available
              </Typography>
            </div>
          )
        ) : null
      ) : null}
      {/* Inner footer */}
      {type === InsightType.PROFILE_VIEW ? (
        <InsightsAccordionFooter
          onClick={() => {
            if (expanded || onlyOneExperience) {
              if (publicId) {
                window.open(
                  Constants.linkedinExperiencesRedirectUrl(publicId),
                  '_blank',
                );
              }
            } else {
              setExpanded((prev) => !prev);
            }
          }}
          redirectText={
            expanded || onlyOneExperience
              ? 'Go to Experience'
              : `Show more experience (${
                  (options && 'totalExperiences' in options
                    ? options?.totalExperiences
                    : undefined) ??
                  (options && 'workExperience' in options
                    ? options?.workExperience.length
                    : undefined) ??
                  0
                })`
          }
          showRedirectIcon={expanded || onlyOneExperience}
          showBorder={true}
        />
      ) : expanded ? (
        type === InsightType.SOCIAL_PRESENCE ? (
          <InsightsAccordionFooter
            onClick={() => {
              if (publicId) {
                window.open(
                  (options && 'url' in options ? options.url : undefined) ??
                    Constants.linkedinActivityRedirectUrl(publicId),
                  '_blank',
                );
              }
            }}
            redirectText="Go to Activity"
          />
        ) : type === InsightType.JOB_OPENINGS ? (
          <InsightsAccordionFooter
            onClick={() => {
              if (prospect?.account?.linkedin) {
                window.open(
                  transformToValidUrl(prospect?.account?.linkedin + '/jobs') ??
                    undefined,
                  '_blank',
                );
              }
            }}
            redirectText={`Go to Jobs ${
              options && 'total' in options && options.total > 0
                ? options.total
                : ''
            }`}
          />
        ) : type === InsightType.COMPANY_NEWS ? (
          <InsightsAccordionFooter
            onClick={() => {
              if (prospect?.account?.linkedin) {
                window.open(
                  transformToValidUrl(
                    prospect?.account?.linkedin + '/posts/?feedView=all',
                  ) ?? undefined,
                  '_blank',
                );
              }
            }}
            redirectText="Go to News"
          />
        ) : type === InsightType.FUNDING_ROUNDS ? (
          options && 'crunchbaseUrl' in options && options.crunchbaseUrl ? (
            <InsightsAccordionFooter
              onClick={() => {
                if (options.crunchbaseUrl || prospect?.account?.linkedin) {
                  window.open(
                    transformToValidUrl(
                      options.crunchbaseUrl ?? prospect?.account?.linkedin,
                    ) ?? undefined,
                    '_blank',
                  );
                }
              }}
              redirectText="View on Crunchbase"
            />
          ) : null
        ) : null
      ) : null}
    </InsightsAccordionWrapper>
  );
};

export const isFundingRoundsVisible = (option?: FundingData | null) => {
  //at least announcedDate should be present to render the UI
  return !!option?.announcedDate;
};

export default InsightsAccordion;
